import React from 'react';
import { motion, useTransform, MotionValue } from 'framer-motion';
import { Shield, EyeOff, ScrollText, Database, ArrowRight } from 'lucide-react';
import { lerp } from '../scrollTimeline';
interface SecurityProxyProps {
  scrollYProgress: MotionValue<number>;
  contentStart: number;
  contentEnd: number;
}
export function SecurityProxy({
  scrollYProgress,
  contentStart,
  contentEnd
}: SecurityProxyProps) {
  // Staggered fade-in for the feature cards
  const card1Opacity = useTransform(scrollYProgress, [lerp(contentStart, contentEnd, 0.14), lerp(contentStart, contentEnd, 0.43)], [0, 1]);
  const card1Y = useTransform(scrollYProgress, [lerp(contentStart, contentEnd, 0.14), lerp(contentStart, contentEnd, 0.43)], [40, 0]);
  const card2Opacity = useTransform(scrollYProgress, [lerp(contentStart, contentEnd, 0.43), lerp(contentStart, contentEnd, 0.71)], [0, 1]);
  const card2Y = useTransform(scrollYProgress, [lerp(contentStart, contentEnd, 0.43), lerp(contentStart, contentEnd, 0.71)], [40, 0]);
  const card3Opacity = useTransform(scrollYProgress, [lerp(contentStart, contentEnd, 0.71), lerp(contentStart, contentEnd, 1.0)], [0, 1]);
  const card3Y = useTransform(scrollYProgress, [lerp(contentStart, contentEnd, 0.71), lerp(contentStart, contentEnd, 1.0)], [40, 0]);
  // Flow diagram animation
  const flowOpacity = useTransform(scrollYProgress, [lerp(contentStart, contentEnd, 0.0), lerp(contentStart, contentEnd, 0.29)], [0, 1]);
  const flowLineProgress = useTransform(scrollYProgress, [lerp(contentStart, contentEnd, 0.14), lerp(contentStart, contentEnd, 0.86)], [0, 1]);
  return <div className="w-full max-w-5xl mx-auto flex flex-col justify-center gap-10 px-4" data-id="element-508">
      {/* Header */}
      <motion.div style={{
      opacity: flowOpacity
    }} className="text-center" data-id="element-509">
        <p className="text-lg text-neutral-400 max-w-2xl mx-auto font-body" data-id="element-510">
          Your agent never touches credentials directly. Every request is
          proxied, masked, and logged.
        </p>
      </motion.div>

      {/* Flow diagram */}
      <motion.div style={{
      opacity: flowOpacity
    }} className="flex items-center justify-center gap-3 md:gap-4" data-id="element-511">
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-neutral-900 border border-neutral-800" data-id="element-512">
          <div className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center" data-id="element-513">
            <span className="text-sm" data-id="element-514">🤖</span>
          </div>
          <span className="text-sm font-medium text-neutral-300" data-id="element-515">Agent</span>
        </div>

        <div className="flex items-center gap-1 text-neutral-600" data-id="element-516">
          <motion.div style={{
          scaleX: flowLineProgress
        }} className="h-px w-8 md:w-16 bg-neutral-600 origin-left" data-id="element-517" />
          <ArrowRight className="w-3 h-3" data-id="element-518" />
        </div>

        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-950/50 border border-emerald-800/50 shadow-lg shadow-emerald-900/20" data-id="element-519">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center" data-id="element-520">
            <Shield className="w-4 h-4 text-emerald-400" data-id="element-521" />
          </div>
          <div className="flex flex-col" data-id="element-522">
            <span className="text-sm font-medium text-emerald-300" data-id="element-523">
              Gateway
            </span>
            <span className="text-[10px] text-emerald-500/70 uppercase tracking-wider" data-id="element-524">
              Proxy
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1 text-neutral-600" data-id="element-525">
          <motion.div style={{
          scaleX: flowLineProgress
        }} className="h-px w-8 md:w-16 bg-neutral-600 origin-left" data-id="element-526" />
          <ArrowRight className="w-3 h-3" data-id="element-527" />
        </div>

        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-neutral-900 border border-neutral-800" data-id="element-528">
          <div className="w-8 h-8 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-center" data-id="element-529">
            <span className="text-sm" data-id="element-530">⚡</span>
          </div>
          <span className="text-sm font-medium text-neutral-300" data-id="element-531">Service</span>
        </div>
      </motion.div>

      {/* Feature cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto w-full" data-id="element-532">
        <motion.div style={{
        opacity: card1Opacity,
        y: card1Y
      }} className="p-5 rounded-xl bg-neutral-900/60 border border-neutral-800 flex flex-col gap-3" data-id="element-533">
          <div className="flex items-center gap-3" data-id="element-534">
            <div className="w-10 h-10 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center" data-id="element-535">
              <EyeOff className="w-5 h-5 text-red-400" data-id="element-536" />
            </div>
            <h4 className="text-base font-medium text-white" data-id="element-537">Fully Masked</h4>
          </div>
          <p className="text-sm text-neutral-400 leading-relaxed font-body" data-id="element-538">
            Credentials are never exposed to the agent. API keys, tokens, and
            connection strings stay locked in the vault.
          </p>
        </motion.div>

        <motion.div style={{
        opacity: card2Opacity,
        y: card2Y
      }} className="p-5 rounded-xl bg-neutral-900/60 border border-neutral-800 flex flex-col gap-3" data-id="element-539">
          <div className="flex items-center gap-3" data-id="element-540">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center" data-id="element-541">
              <ScrollText className="w-5 h-5 text-amber-400" data-id="element-542" />
            </div>
            <h4 className="text-base font-medium text-white" data-id="element-543">Fully Audited</h4>
          </div>
          <p className="text-sm text-neutral-400 leading-relaxed font-body" data-id="element-544">
            Every request is logged with full context — what was asked, what was
            sent, what came back. Complete audit trail.
          </p>
        </motion.div>

        <motion.div style={{
        opacity: card3Opacity,
        y: card3Y
      }} className="p-5 rounded-xl bg-neutral-900/60 border border-neutral-800 flex flex-col gap-3" data-id="element-545">
          <div className="flex items-center gap-3" data-id="element-546">
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center" data-id="element-547">
              <Database className="w-5 h-5 text-purple-400" data-id="element-548" />
            </div>
            <h4 className="text-base font-medium text-white" data-id="element-549">
              Write Protection
            </h4>
          </div>
          <p className="text-sm text-neutral-400 leading-relaxed font-body" data-id="element-550">
            Database connections are read-only by default. Write access requires
            explicit opt-in per credential, per agent.
          </p>
        </motion.div>
      </div>
    </div>;
}